import { type ReactNode, useState } from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { dealNoticeFor } from '../../deals/components/deal-next-action';
import {
  ConfirmRelayDialog,
  confirmRelaySuccessMessage,
  upstreamLimitRefusal,
} from '../components/relay-queue-table';
import { type ConfirmRelayPayload } from '../types';
import { useRelayAction, useRelayClaim } from './use-bid-mutations';

/**
 * «Оброблено» / «Обробити» = the bid was placed on the real auction = relayed
 * (owner, 2026-10-25/26). ONE flow for every surface that offers it — the bid
 * page's toggle and the overview's relay queue: take the relay claim, open the
 * desk's own confirm-relay form (one-click confirm, «На аукціоні вже вища
 * ставка» + raise step), and on confirm the server flips the bid to `relayed`
 * and writes `processedOnAuction` with it.
 *
 * Refusals: a colleague's claim or a stale bid is toasted and `onChanged`
 * re-reads the caller's data (closing the form); a refusal the operator can act
 * on (an exposure limit, a malformed reference) keeps the form open with the
 * reason toasted, so nothing chosen is lost.
 */
export function useMirrorBid(onChanged: () => Promise<unknown> | void): {
  start: (bidId: string, bidChf: number) => Promise<void>;
  busy: boolean;
  /** The confirm-relay form while it is open, else null — render it once. */
  dialog: ReactNode;
} {
  const relayMutation = useRelayAction();
  const claimMutation = useRelayClaim();
  const [target, setTarget] = useState<{ bidId: string; bidChf: number } | null>(null);
  const busy = relayMutation.isPending || claimMutation.isPending;

  const start = async (bidId: string, bidChf: number) => {
    if (busy) return;
    try {
      await claimMutation.mutateAsync({ id: bidId, action: 'claim' });
      setTarget({ bidId, bidChf });
    } catch (claimError) {
      const notice = dealNoticeFor(claimError);
      toast.error(notice.text);
      if (notice.refresh) await onChanged();
    }
  };

  const submit = async (payload: ConfirmRelayPayload) => {
    if (!target || busy) return;
    try {
      const result = await relayMutation.mutateAsync({
        id: target.bidId,
        action: 'confirm-relay',
        body: { ...payload },
      });
      const decision = (result as { decision?: unknown } | null | undefined)?.decision;
      toast.success(
        decision === 'outbid' || decision === 'raise'
          ? confirmRelaySuccessMessage(result)
          : t('admin.deals.notice.done'),
      );
      setTarget(null);
      await onChanged();
    } catch (relayError) {
      // «У клієнта автоставка…» is the dialog's own next step — hand it back.
      if (upstreamLimitRefusal(relayError)) throw relayError;
      const notice = dealNoticeFor(relayError);
      toast.error(notice.text);
      if (notice.refresh) {
        setTarget(null);
        await onChanged();
      }
    }
  };

  // Mounted only while open, so every opening starts from a clean form.
  const dialog = target ? (
    <ConfirmRelayDialog
      item={{ bid: target.bidChf }}
      busy={busy}
      onCancel={() => setTarget(null)}
      onSubmit={submit}
    />
  ) : null;

  return { start, busy, dialog };
}
